"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { StringInput } from "@/components/ui/shared/SpecialInput";
import { referCustomer } from "@/lib/database/client/database";
import { getCurrentUser } from "@/lib/db/users";
import { createClient } from "@/utils/supabase/client";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

export default function Referral() {
  const supabase = createClient();

  const [userValid, setUserValid] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [referralName, setReferralName] = useState("");
  const [referralEmail, setReferralEmail] = useState("");
  const [whyNowadays, setWhyNowadays] = useState("");
  const [fullNameTouched, setFullNameTouched] = useState(false);

  const handleFullNameChange = (value: string) => {
    setFullNameTouched(true);
    setFullName(value);
  };

  const [emailTouched, setEmailTouched] = useState(false);

  const handleEmailChange = (value: string) => {
    setEmailTouched(true);
    setEmail(value);
  };

  const [showThankYou, setShowThankYou] = useState(false);

  const [referralNameTouched, setReferralNameTouched] = useState(false);
  const [referralEmailTouched, setReferralEmailTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  // Email validation helper
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Update handlers for referral fields
  const handleReferralNameChange = (value: string) => {
    setReferralNameTouched(true);
    setReferralName(value);
  };
  const handleReferralEmailChange = (value: string) => {
    setReferralEmailTouched(true);
    setReferralEmail(value);
  };

  const submitForm = async () => {
    setFullNameTouched(true);
    setEmailTouched(true);
    setReferralNameTouched(true);
    setReferralEmailTouched(true);
    setSubmitStatus("idle");
    setSubmitMessage("");

    // Validate required fields
    if (
      !fullName ||
      !email ||
      !referralName ||
      !referralEmail ||
      !isValidEmail(email) ||
      !isValidEmail(referralEmail)
    ) {
      setSubmitStatus("error");
      setSubmitMessage("Please fill in all required fields with valid information.");
      return;
    }

    setSubmitting(true);
    try {
      // Save to database using existing function
      await referCustomer(supabase, fullName, email, referralName, referralEmail, whyNowadays);

      // Send referral email via API
      const response = await fetch("/api/referral-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          referredEmail: referralEmail,
          referredName: referralName,
          referrerName: fullName,
          referrerEmail: email,
          reason: whyNowadays || "planning corporate events",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send referral email");
      }

      // Clear the form
      setFullName("");
      setEmail("");
      setReferralName("");
      setReferralEmail("");
      setWhyNowadays("");
      setShowThankYou(true);
      setFullNameTouched(false);
      setEmailTouched(false);
      setReferralNameTouched(false);
      setReferralEmailTouched(false);
    } catch (err) {
      setSubmitStatus("error");
      setSubmitMessage("Submission failed. Please try again later.");
      // Optionally log error
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    (async () => {
      const user = await getCurrentUser(supabase);

      if (user) {
        setUserValid(true);
        setFullName(user.first_name + " " + user.last_name);
        setEmail(user.email);
      }
    })();
  }, []);

  useEffect(() => {
    if (
      submitStatus === "error" &&
      submitMessage === "Please fill in all required fields with valid information." &&
      fullName &&
      email &&
      referralName &&
      referralEmail &&
      isValidEmail(email) &&
      isValidEmail(referralEmail)
    ) {
      setSubmitStatus("idle");
      setSubmitMessage("");
    }
  }, [fullName, email, referralName, referralEmail, submitStatus, submitMessage]);

  return (
    <div>
      {/* Hero Section with Left-Right Layout */}
      <section className="container-top-form">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          {/* Left Content */}
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-6">
              <h2 className="text-h2 text-landing-primary">Refer a Friend & Get $250</h2>
              <p className="text-body-lg text-landing-secondary max-w-530">
                After the person you referred books their first offsite or event with Nowadays, you will each receive a
                $250 Visa gift card.
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0"></div>
                  <span className="text-body text-landing-secondary">
                    Only valid for new customers that book events over a $20K budget
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0"></div>
                  <span className="text-body text-landing-secondary">
                    Both you and your referral receive $250 Visa gift cards
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0"></div>
                  <span className="text-body text-landing-secondary">
                    No limit on referrals - refer as many friends as you want!
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="flex items-start justify-center">
            <motion.div
              className="w-full max-w-md"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="space-y-6">
                {/* Your Information */}
                <div className="space-y-4">
                  <h3 className="text-h4 text-landing-primary">Your Information</h3>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Your Full Name*</label>
                    <StringInput
                      disabled={userValid}
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: John Doe"
                      value={fullName}
                      setValue={handleFullNameChange}
                      validator={fullNameTouched ? fullName !== "" : true}
                    />
                    {fullNameTouched && !fullName && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        Your name is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Your Email*</label>
                    <StringInput
                      disabled={userValid}
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: johndoe@gmail.com"
                      value={email}
                      setValue={handleEmailChange}
                      validator={emailTouched ? email !== "" && isValidEmail(email) : true}
                    />
                    {emailTouched && (!email || !isValidEmail(email)) && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        Please enter a valid email.
                      </div>
                    )}
                  </div>
                </div>

                {/* Referral Information */}
                <div className="space-y-4">
                  <h3 className="text-h4 text-landing-primary">Referral Information</h3>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">
                      Full Name of the Person You're Referring*
                    </label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: Jane Doe"
                      value={referralName}
                      setValue={handleReferralNameChange}
                      validator={referralNameTouched ? referralName !== "" : true}
                    />
                    {referralNameTouched && !referralName && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        Referral name is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">
                      Email of the Person You're Referring*
                    </label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: janedoe@gmail.com"
                      value={referralEmail}
                      setValue={handleReferralEmailChange}
                      validator={referralEmailTouched ? referralEmail !== "" && isValidEmail(referralEmail) : true}
                    />
                    {referralEmailTouched && (!referralEmail || !isValidEmail(referralEmail)) && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        Please enter a valid email.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">
                      Why Do You Think They Would Be a Good Fit for Nowadays?
                    </label>
                    <textarea
                      className="w-full h-24 input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors resize-none"
                      placeholder="Tell us why they would be a great fit..."
                      value={whyNowadays}
                      onChange={(e) => setWhyNowadays(e.target.value)}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  {submitStatus !== "idle" && (
                    <div
                      className={`p-3 rounded-md text-sm ${
                        submitStatus === "success"
                          ? "bg-green-50 text-green-700 border border-green-200 mb-4"
                          : "bg-red-50 text-red-700 border border-red-200 mb-4"
                      }`}
                    >
                      {submitMessage}
                    </div>
                  )}
                  <Button variant="primary" size="md" onClick={submitForm} className="w-full" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit Referral"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Thank You Modal */}
      {showThankYou && (
        <div
          className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={() => setShowThankYou(false)}
        >
          <motion.div
            className="bg-white p-8 rounded-2xl max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center space-y-4">
              <div className="text-4xl">🎉</div>
              <h2 className="text-h3 text-landing-primary font-bold">Thank You!</h2>
              <p className="text-body text-landing-secondary">
                Your referral has been submitted successfully. We'll be in touch soon!
              </p>
              <Button variant="primary" size="md" onClick={() => setShowThankYou(false)}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
